import React from 'react';
import { Box, TextField, IconButton, Grid, Typography, Checkbox, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuid } from 'uuid';

interface RangeItem {
  id: string;
  start: string;
  end: string;
  price: string;
  applyModifierWeightUpcharge: boolean;
}

interface WeightPriceRangesProps {
  ranges: RangeItem[];
  onChange: (ranges: RangeItem[]) => void;
}

export function WeightPriceRanges({ ranges, onChange }: WeightPriceRangesProps) {
  const handleAddRange = () => {
    onChange([...ranges, { id: uuid(), start: '', end: '', price: '', applyModifierWeightUpcharge: true }]);
  };

  const handleRemoveRange = (id: string) => {
    onChange(ranges.filter(range => range.id !== id));
  };

  const handleRangeChange = (id: string, field: keyof RangeItem, value: string | boolean) => {
    onChange(
      ranges.map(range => 
        range.id === id ? { ...range, [field]: value } : range
      )
    );
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Weight Price Ranges
      </Typography>
      
      {ranges.map((range, index) => (
        <Grid container spacing={2} key={range.id} sx={{ mb: 2 }}>
          <Grid item xs={2.5}>
            <TextField
              label="Start"
              type="number"
              value={range.start}
              onChange={(e) => handleRangeChange(range.id, 'start', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={2.5}>
            <TextField
              label="End"
              type="number"
              value={range.end}
              onChange={(e) => handleRangeChange(range.id, 'end', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="Price"
              type="number"
              value={range.price}
              onChange={(e) => handleRangeChange(range.id, 'price', e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <Typography variant="body2">$</Typography>,
              }}
            />
          </Grid>
          <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={range.applyModifierWeightUpcharge}
                  onChange={(e) => handleRangeChange(range.id, 'applyModifierWeightUpcharge', e.target.checked)}
                  size="small"
                />
              }
              label="Apply Upcharge"
              sx={{ fontSize: '0.875rem' }}
            />
          </Grid>
          <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => handleRemoveRange(range.id)}
              disabled={ranges.length <= 1}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      
      <Box sx={{ mt: 1 }}>
        <IconButton onClick={handleAddRange} color="primary">
          <AddIcon />
        </IconButton>
        <Typography variant="body2" component="span">
          Add Range
        </Typography>
      </Box>
    </Box>
  );
}
